import React from "react";

import iconChevron from "@app/images/picto/chevron-expand.svg";
import moment from 'moment';
import Select from 'react-select';
import { useTranslation } from "react-i18next";
import { OverlayTrigger } from "react-bootstrap";
import { Tooltip } from "react-bootstrap";
import SelectList from "../2_widgets/SelectList";

/**
 * 
 * @param {*} columns: Array of columns to select to display on table 
 * It uses following keys : 
 *  key : Key relative to the object to display in the table
 *  label : Column header text
 *  sortable : Indicate if the column is sortable
 *  onlyButtons : Display only buttons in the column
 *  selectable : Each line of the table can be selectable and linked to an action (delete, edit...)
 *  buttons: List of buttons with parameters {callback: function to call}, {label: Text to display}
 *  types: 
 *      date > To display Date type
 *      badge > To display badge - CSS is based on value key name
 * Example of columns array
 *     let selectedColumns = [
        {key: "name", label: "Entreprise", sortable: true}, 
        {key: "is_sre_member", label: "Membre SRE", sortable: false, translatable: true}, 
        {key: "is_chart_signatory", label: "Charte 7+", sortable: false}, 
        {key: "roles.role", label: "Rôles", sortable: false, type: "badge"}, 
        {key: "agreed_to_general_condition", label: "Consentement", sortable: true},
        {key: "buttons", label: "Validation", onlyButtons: true, buttons: rowButtons, }        
    ]
 * Set Filters as followed: [{
        key: "is_sre_member", 
        label: "Filtre SRE", 
        options: [
            {value: "all", label: "Tous"}, 
            {value: true, label: "Membres"}, 
            {value: false, label: "Non-Membres"}, 
        ]
    },
 *  @returns 
 */
export default function TableContent({
    tableData, 
    columns, 
    sort,
    filters=[], 
    filterCallback, 
    statusFilters=[], 
    selectedRowIds=[],
    handleRowSelection,
    handleAllCheckedSelection
}) {
    const { t } = useTranslation();
    
    /**
     * Display table content based on the key
     * @param {*} data 
     * @returns 
     */
    function displayRow(data, index) {
        const rowId = data.id;
        let cols = columns.map((c) => {
            // Display cell with only buttons
            if (c.onlyButtons) { 
                let buttons = c.buttons.map((button, index) => {             
                    if (button.condition && !button.condition(data)) {                        
                        return;
                    }

                    if (button.icon) {
                        return (
                            <OverlayTrigger 
                                key={`${c.key}-${index}`}
                                overlay={
                                    <Tooltip id={`tooltip-${index}`} className="icon-tooltip">{button.toolTip}</Tooltip>
                                }>
                                <img 
                                    key={index} 
                                    className="table-icon-button" 
                                    src={button.icon}
                                    onClick={() => button.callback(data)} 
                                    alt={`${button.toolTip} l'article`}
                                />
                            </OverlayTrigger>
                        )
                    } else 
                        return (
                            <button 
                                key={`${c.key}-${index}-${button.label}`} 
                                className="custom-btn-secondary-table ms-1" 
                                onClick={() => button.callback(data)} alt="callback button">
                                {button.label}
                            </button>
                        )
                });

                return (<td key={`${c.key}-${index}`}>{buttons}</td>);

            // If checkbox column
            } else if (c.isSelectable) {
                const isChecked = selectedRowIds.includes(rowId);
                return (
                    <td key={`${c.key}-${index}`} className="checkbox-cell">
                        <input 
                            id={`checkbox-${rowId}`}
                            className="form-check-input" 
                            type="checkbox" 
                            checked={isChecked}
                            onChange={(e) => handleRowSelection(rowId, e.target.checked)}                            
                        >
                        </input>
                    </td>
                )
            }
            else {                
                // Display cell value
                return (<td key={`${c.key}-${index}`}>{formatCell(c, data)}</td>)
            }                
        });        
                
        return (
            // Display line
            <tr key={`${rowId}-${index}`}>
                {cols}
            </tr>
        )
    }

    /**
     * Format the cell depending on the column type
     * @param {*} column 
     * @param {*} data 
     * @returns 
     */
    function formatCell(column, data) {
        let value = data[column.key];        
        
        switch(column.type) {
            case "date":
                return formatCellDate(value);
            case "badge": 
                return formatCellBadge(value, column.translatable);
            default:
                return formatCellDefault(value, column.translatable);
        }
    }

    /**
     * Display table header
     * @param {} column 
     * @returns 
     */
    function displayTableHeader(column, index) {
        let sortable = "";
        let headerContent = column.label;
        let checkboxCellClassName = "";

        if (column.sortable) {
            sortable = <img 
                className="icon-filter-column"  
                src={iconChevron}  
                alt="Filter by" 
                onClick={() => sort(column.key)}
            />
        } 
        
        // Display checkbox
        if ( column.isSelectable) {
            checkboxCellClassName = "checkbox-cell";
            headerContent = (<input
                type="checkbox"
                className="form-check-input"
                onChange={ (e) => handleAllCheckedSelection(e.target.checked)}
            />)
        }

        return (
            <th key={`${column.key}-${index}`} className={checkboxCellClassName}>
                {headerContent}
                {sortable}
            </th>
        )   
    }

    /**
     * Display table filter 
     * @param {*} filter 
     * @param {*} index 
     * @returns 
     */
    function displayFilter(filter, index) {        
        return (
            <div key={index} className="col-3">
                <SelectList
                    options={filter.options}
                    callbackOnChange={(option) => filterCallback(option, filter)}
                    placeholder={filter.label}
                />
            </div>
        )
    }

    function displayStatusesFilter() {
        if (statusFilters?.length > 0) {
            let result = [
                <div 
                    key="view-all" 
                    className="status-filter-col text-center">
                        <span 
                            className="status-filter" 
                            onClick={() => filterCallback({value: "all"}, {key: "status"})} 
                            href="#">
                            Voir tous
                        </span>                        
                </div>
            ];
            result.push(statusFilters.map((status) => {
                return (
                    <div 
                        key={status} 
                        className="status-filter-col text-center">
                            <span 
                                className="status-filter" 
                                onClick={() => filterCallback({value: status}, {key: "status"})} 
                                href="#">
                                {t(status)}
                            </span>
                    </div>
                );
            }));
            
            return (
                <div className="status-row">
                    {result}
                </div>                
            );
        }
    }

    function displayFilterSection() {
        if (filters?.length > 0 || statusFilters?.length > 0)
            return (
                <div className="custom-table-filter">
                    {displayStatusesFilter()}
                    <div className="row">                        
                        {filters.map(displayFilter)}
                    </div>                
                </div>        
            )      
    }

    function formatCellDefault(value, translatable=false) {            
        if (value != null && !Array.isArray(value)) {
            let text = translatable ? t(value.toString()) : value.toString();
            return (text);
        }            
        else if (value != null && Array.isArray(value)) {
            let result = [];
            value.forEach((v, index) => {
                let text = translatable ? t(v.toString()) : v.toString();
                result.push(<span key={index}><u>{text}</u> </span>);
            })
            return result;
        }    
    }

    function formatCellBadge(value, translatable=false) {
        
        if (value != null && !Array.isArray(value)) {
            let text = translatable ? t(value.toString()) : value.toString();
            return (
                <span className={"badge badge-" + value.toString()}>
                    {text}
                </span>
            );
        }
        else if (value != null && Array.isArray(value)) {
            let result = [];
            
            value.forEach((v, index) => {
                let text = translatable ? t(v.toString()) : v.toString();
                result.push(
                    <span key={index} className={"badge badge-" + v.toString()}>
                        {text} 
                    </span>
                );
            })
            return result;
        }
    }

    return (
        <div className="custom-table-component">
            {displayFilterSection()}
            <table className="custom-table">
                <thead>
                    <tr>
                        {columns.map(displayTableHeader)}
                    </tr>
                </thead>
                <tbody>
                    {tableData.map(displayRow)}                
                </tbody>
            </table>
        </div>
    )
}


function formatCellDate(value) {
    if (value)
        return moment(value).format("DD/MM/YYYY");
    else
        return "";
}